import React, { useMemo } from "react";

import {
  AutocompleteRenderOptionState,
  createFilterOptions,
} from "@material-ui/lab";
import { sortBy } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { BusinessAction } from "actions";

import {
  Autocomplete,
  FILTER_LIMIT,
  InlineButton,
} from "components/Form/FormikControls";

import {
  BusinessFieldFilterOptions,
  BUYER,
  DefaultBusinessFieldFilterOptions,
  VENDOR,
} from "constants/businesses";

import { EMPTY_ARRAY, EMPTY_OBJECT } from "lib";

import { isBusinessActive } from "lib/businesses";
import { caselessCompare } from "lib/compare";
import { getDefaultTransitInsuranceValues } from "lib/insuranceRates";
import { getLivestockSaleId } from "lib/navigation";

import {
  getCurrentSpeciesId,
  getDefaultBillingSettings,
  getIsSaleyardAdmin,
  getProperties,
  selectConsignmentIdsByVendorIdLookup,
  selectOptionsForBusinessField,
  selectSaleLotIdsByBuyerIdLookup,
} from "selectors";

import {
  useHasTemporaryBusinessesPermission,
  useFieldSetter,
  useFieldValue,
  useUpdateFormWithOfflineLookup,
} from "hooks";
import { PopperProps } from "@material-ui/core";
import { Species } from "constants/species";

const getOptionValue = option => option?.id;

interface BusinessFieldProps {
  autoFocus?: boolean;
  disabled?: boolean;
  label: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Business ID or null
  onChangeExtra?: (newValue: any) => void;
  sortRecentBusinessType?: string;
  allowAddNew?: boolean;
  activeOnly?: boolean;
  inlineButton?: InlineButton;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Business option filter callback
  extraFilter?: (o: any) => boolean;
  ListboxComponent?: React.ComponentType<React.HTMLAttributes<HTMLElement>>;
  ListboxProps?: object;
  PopperComponent?: React.ComponentType<PopperProps>;
  renderOption?: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Business option object
    option: any,
    state: AutocompleteRenderOptionState,
  ) => React.ReactNode;
  renderOptionHeadings?: boolean;
  hideFilteredCount?: boolean;
  required?: boolean;
  placeholder?: string;
  openOnFocus?: boolean;

  /**
   * true: include only globals
   false: exclude globals
   undefined | null: no filter
   */
  globalBusinesses?: boolean;
  excludeIds?: string[];
  searchFields?: string[];
  // Each section's options are floated to the top, in order, under its
  // heading - the rest follow under "Existing Businesses".
  prioritisedSections?: PrioritisedSection[];
  // Listed after Existing Businesses, once something is typed.
  trailingSections?: PrioritisedSection[];
  // Non-Business options (e.g. side-loaded records), in the same shape the
  // option renderer expects - only listed within the sections.
  extraOptions?: BusinessFieldExtraOption[];
  // Until something is typed, list only the prioritised options.
  searchToListAll?: boolean;
  onInputChange?: (event: unknown, value: string) => void;
}

export interface BusinessFieldExtraOption {
  id: string;
  name: string;
  label: string;
  abn?: string | null;
  primaryContactEmail?: string | null;
  properties: { PIC: string }[];
  isActive: boolean;
  isGlobal: boolean;
  subsection?: string;
}

interface PrioritisedSection {
  heading: string;
  ids: string[];
}

const sectionHeading = (label: string) => ({
  disabled: true,
  isSectionHeading: true,
  label,
});

// Options with a `subsection` (eg a legacy record's data set) are grouped
// under a subheading for it, in the order the subsections first appear.
const withSubsections = <T extends { subsection?: string }>(
  options: T[],
): (T | { disabled: true; isSubsectionHeading: true; label: string })[] => {
  if (!options.some(option => option.subsection)) {
    return options;
  }
  const bySubsection = new Map<string, typeof options>();
  options.forEach(option => {
    const key = option.subsection || "Other";
    bySubsection.set(key, [...(bySubsection.get(key) || []), option]);
  });
  return Array.from(bySubsection).flatMap(([label, groupOptions]) => [
    { disabled: true, isSubsectionHeading: true, label },
    ...groupOptions,
  ]);
};

export const BusinessField: React.FC<BusinessFieldProps> = ({
  autoFocus = false,
  disabled = false,
  label,
  name,
  onChangeExtra = undefined,
  sortRecentBusinessType = undefined,
  allowAddNew = true,
  activeOnly = false,
  inlineButton = null,
  extraFilter = undefined,
  ListboxComponent = undefined,
  ListboxProps = undefined,
  PopperComponent = undefined,
  renderOption = undefined,
  renderOptionHeadings = undefined,
  globalBusinesses = null,
  excludeIds = [],
  hideFilteredCount = false,
  required = false,
  placeholder = undefined,
  openOnFocus = undefined,
  searchFields = DefaultBusinessFieldFilterOptions,
  prioritisedSections = EMPTY_ARRAY,
  extraOptions = EMPTY_ARRAY,
  searchToListAll = false,
  trailingSections = EMPTY_ARRAY,
  onInputChange = undefined,
}) => {
  const propertyByIdLookup = useSelector(getProperties);

  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);

  // include buyer pics in the filter search
  const filter = createFilterOptions({
    matchFrom: "any",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Business option object
    stringify: (option: any) => {
      const filterString = searchFields.map(searchField => {
        switch (searchField) {
          case BusinessFieldFilterOptions.code:
            return isSaleyardAdmin
              ? option.shortCodeSaleyard
              : option.shortCode;
          case BusinessFieldFilterOptions.properties:
            return (
              option?.properties
                // Non-Business options carry their PIC directly.
                ?.map(
                  property =>
                    property.PIC ?? propertyByIdLookup[property.id]?.PIC,
                )
                .filter(Boolean)
                .join(", ")
            );
          default:
            return option[searchField];
        }
      });

      return filterString.join(" ");
    },
  });
  const dispatch = useDispatch();
  const setValue = useFieldSetter(name);
  const selectedBusinessId = useFieldValue(name);

  const currentSpecies = useSelector(getCurrentSpeciesId);
  const billingSettings = useSelector(getDefaultBillingSettings);
  // Temporary Businesses only exist within a Sale.
  const hasTemporaryBusinesses =
    useHasTemporaryBusinessesPermission() && Boolean(getLivestockSaleId());

  const [tempBusinesses, setTempBusinesses] = React.useState([]);

  const addNewBusiness = businessName => {
    const tempId = uuidv4();
    dispatch(
      BusinessAction.create(
        {
          id: tempId,
          name: businessName.trim(),
          ...getDefaultTransitInsuranceValues(billingSettings),
          isTemporary: hasTemporaryBusinesses,
        },
        { changeReason: "Created via a business field." },
      ),
    );

    setTempBusinesses([...tempBusinesses, { id: tempId, name: businessName }]);
    // Add a temp option so its selected straight away.

    setValue(tempId);
  };

  const businessLookup =
    useSelector(selectOptionsForBusinessField) || EMPTY_OBJECT;

  const selectedBusiness: Business = businessLookup[selectedBusinessId];

  const saleLotIdsByBuyerIdLookup = useSelector(
    selectSaleLotIdsByBuyerIdLookup,
  );

  const consignmentIdsByVendorIdLookup = useSelector(
    selectConsignmentIdsByVendorIdLookup,
  );

  // Sort options by top buyers first, then the relevant [saleyard|agent] code.
  const options = useMemo(() => {
    const allOptions = [...Object.values(businessLookup), ...tempBusinesses];

    // Allow for (now) inactive business on existing data
    if (
      selectedBusiness &&
      !isBusinessActive(selectedBusiness, getLivestockSaleId())
    ) {
      allOptions.push(selectedBusiness);
    }
    const sortOrder = [
      // In Sale already.
      sortRecentBusinessType === BUYER
        ? o => saleLotIdsByBuyerIdLookup[o.id]
        : null,
      sortRecentBusinessType === VENDOR
        ? o => consignmentIdsByVendorIdLookup[o.id]
        : null,
      // Top Buyer (Invert, so that we sort true (1) to the top.)
      currentSpecies === Species.CATTLE ? o => !o.isTopBuyerCattle : null,
      currentSpecies === Species.SHEEP ? o => !o.isTopBuyerSheep : null,
      "name",
      // Short Code
      isSaleyardAdmin ? "shortCodeSaleyard" : "shortCode",
    ].filter(Boolean);
    return [...sortBy(allOptions, sortOrder), ...extraOptions];
  }, [
    extraOptions,
    businessLookup,
    tempBusinesses,
    selectedBusiness,
    sortRecentBusinessType,
    currentSpecies,
    isSaleyardAdmin,
    saleLotIdsByBuyerIdLookup,
    consignmentIdsByVendorIdLookup,
  ]);

  useUpdateFormWithOfflineLookup(name);

  // Adjust the filtering function to push a specific short code result to the top.
  // This is defined by the type of sale, and who is searching, so memoize the function call
  const getAdjustedOptionsFilter = useMemo(() => {
    const doesShortCodeMatch = (option: Business, inputValue) =>
      isSaleyardAdmin
        ? caselessCompare(option.shortCodeSaleyard, inputValue)
        : caselessCompare(option.shortCode, inputValue);

    return (filterOptions, params) => {
      let outsideFilterCount = 0;
      type BusinessWithHide = Business & { hide: boolean };
      let filtered = filter(filterOptions, params).filter(
        (o: BusinessWithHide) => {
          // Hide any hidden/inactive businesses
          if (o.hide || (activeOnly && !o.isActive === true)) {
            return false;
          }

          if (excludeIds.includes(o.id)) {
            return false;
          }

          if (globalBusinesses !== null) {
            if (globalBusinesses && !o.isGlobal) {
              return false;
            }
            if (!globalBusinesses && o.isGlobal) {
              return false;
            }
          }

          if (extraFilter) {
            if (extraFilter(o) !== true) {
              outsideFilterCount += 1;
              return false;
            }
          }

          return true;
        },
      );

      const { inputValue } = params;
      // If we have a single match for a business short code, float it to the top.
      // (We don't know if a saleyard)
      filtered = [].concat(
        filtered.filter(option => doesShortCodeMatch(option, inputValue)),
        filtered.filter(option => !doesShortCodeMatch(option, inputValue)),
      );

      const isAwaitingSearch = searchToListAll && !inputValue;
      if (isAwaitingSearch) {
        const sectionedIds = new Set(
          prioritisedSections.flatMap(section => section.ids),
        );
        filtered = filtered.filter(option => sectionedIds.has(option.id));
      }

      // Matches are listed in full - the other sections are capped.
      let isSectioned = false;
      if (prioritisedSections.length) {
        const sectionedIds = new Set(
          prioritisedSections.flatMap(section => section.ids),
        );
        const extraIds = new Set(extraOptions.map(option => option.id));
        const groups: [string, typeof filtered][] = [
          ...prioritisedSections.map(
            ({ heading, ids }): [string, typeof filtered] => [
              heading,
              sortBy(
                filtered.filter(option => ids.includes(option.id)),
                option => ids.indexOf(option.id),
              ),
            ],
          ),
        ];
        const cappedGroup = (
          heading: string,
          groupOptions: typeof filtered,
        ): [string, typeof filtered] => [
          groupOptions.length > FILTER_LIMIT
            ? `${heading} (showing ${FILTER_LIMIT} of ${groupOptions.length.toLocaleString()})`
            : heading,
          groupOptions.slice(0, FILTER_LIMIT),
        ];
        groups.push(
          cappedGroup(
            "Existing Businesses",
            filtered.filter(
              option =>
                !sectionedIds.has(option.id) && !extraIds.has(option.id),
            ),
          ),
          ...trailingSections.map(({ heading, ids }) =>
            cappedGroup(
              heading,
              filtered.filter(
                option =>
                  ids.includes(option.id) && !sectionedIds.has(option.id),
              ),
            ),
          ),
        );
        const nonEmptyGroups = groups.filter(
          ([heading, groupOptions]) => heading && groupOptions.length,
        );
        // No headings when there's only Existing Businesses.
        const hasSectionMatches = groups.some(
          ([heading, groupOptions], index) =>
            index !== prioritisedSections.length &&
            heading &&
            groupOptions.length,
        );
        if (hasSectionMatches) {
          isSectioned = true;
          filtered = nonEmptyGroups.flatMap(([heading, groupOptions]) => [
            sectionHeading(heading),
            ...withSubsections(groupOptions),
          ]);
        }
      }

      const outsideFilterExtraLabel =
        !hideFilteredCount && outsideFilterCount
          ? `(${outsideFilterCount} filtered out)`
          : "";

      if (renderOption && renderOptionHeadings) {
        const heading = {
          disabled: true,
          isHeading: true,
        };
        filtered = [heading].concat(filtered);
      }

      if (filtered.length === 0) {
        filtered.push({
          disabled: true,
          label: isAwaitingSearch
            ? "Type to search all businesses"
            : `No results ${outsideFilterExtraLabel}`,
        });
      } else if (!isSectioned && filtered.length > FILTER_LIMIT) {
        filtered = [
          {
            disabled: true,
            label: `Showing ${FILTER_LIMIT} of ${filtered.length.toLocaleString()} options ${outsideFilterExtraLabel}`,
          },
        ].concat(filtered.slice(0, FILTER_LIMIT));
      } else if (outsideFilterCount > 0 && !hideFilteredCount) {
        filtered = [
          {
            disabled: true,
            label: `${outsideFilterCount} options filtered out`,
          },
        ].concat(filtered);
      }

      // Suggest the creation of a new value
      if (allowAddNew && params.inputValue !== "") {
        filtered.push({
          inputValue: params.inputValue,
          label: hasTemporaryBusinesses
            ? `Create Temporary Business "${params.inputValue}"`
            : `Add "${params.inputValue}"`,
        });
      }

      return filtered;
    };
  }, [
    isSaleyardAdmin,
    filter,
    allowAddNew,
    hasTemporaryBusinesses,
    activeOnly,
    extraFilter,
    renderOption,
    renderOptionHeadings,
    excludeIds,
    globalBusinesses,
    hideFilteredCount,
    prioritisedSections,
    extraOptions,
    searchToListAll,
    trailingSections,
  ]);

  return (
    <Autocomplete
      autoFocus={autoFocus}
      disabled={disabled}
      ListboxComponent={ListboxComponent}
      ListboxProps={ListboxProps}
      filterOptions={getAdjustedOptionsFilter}
      renderOption={renderOption}
      getOptionValue={getOptionValue}
      handleNew={addNewBusiness}
      label={label}
      name={name}
      inlineButton={inlineButton}
      onChangeExtra={onChangeExtra}
      onInputChange={onInputChange}
      options={options}
      PopperComponent={PopperComponent}
      required={required}
      placeholder={placeholder}
      openOnFocus={openOnFocus}
      disableAutoComplete
    />
  );
};
